/***
 * Created on 2023/8/8
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";

function PasswordInput({label, placeholder, required, onChange, showStrength, showTip, metadata, ignoreRules=true, matchInput=null, status, compType='', ..._props}) {
    const { t } = useTranslation();
    const inputRef = useRef();
    const isDirty = useRef(null);
    const [valid, setValid] = useState(true);
    const [visiblePasswd, setVisiblePasswd] = useState(false);
    const [showTipPop, setShowTipPop] = useState(false);
    const rules = useRef({rule_group: [], required_score: 0.0});

    const strengthIndex =  {
        0: "",
        1: "Weak",
        2: "Weak",
        3: "Fair",
        4: "Good",
        5: "Good",
        6: "Strong"
    }

    const toggleVisible = () => {
        setVisiblePasswd(prev => !prev);
    }

    const passwordRuleChecker = (e) => {
        let rule_group = [];
        let required_score = 0.0;
        const emails = metadata?.emails?.map(email => email.email) || [];
        const phones = metadata?.phones?.map(phone => phone.number) || [];
        if ( /[a-z]+/.test(e.target.value) ) {
            rule_group.push('lower_case');
            required_score += 0.5;
        }
        if ( /[A-Z]+/.test(e.target.value) ) {
            rule_group.push('upper_case');
            required_score += 0.5;
        }
        if ( /[0-9]+/.test(e.target.value) ) {
            rule_group.push('number');
            required_score += 1;
        }
        if ( /[!"#$%&'()*+,-.\/:;<=>?@\[\\\]\^_`{|}~]+/.test(e.target.value) ) {
            rule_group.push('special_case');
        }
        if (e.target.value.length >= 8 && e.target.value.length <= 50) {
            rule_group.push('length_reached');
            required_score += 1;
        }
        if (!emails.concat(phones).includes(e.target.value) && e.target.value !== "") {
            rule_group.push('qid_checked');
            required_score += 1;
        }
        rules.current = {rule_group: rule_group, required_score: required_score}
        return {rule_group, required_score};
    }

    const onInputChange = (e) => {
        passwordRuleChecker(e);

        // this is a way to escape initial validation check on html5 form
        if (required) {
            inputRef.current.setAttribute('required', true);
        }

        if (typeof onChange === 'function') {
            onChange(e, () => {
                isDirty.current = true;
            });
        }
        else {
            isDirty.current = true;
        }
    }

    const onInputFocus = () => {
        if (showTip) {
            setShowTipPop(true);
        }
    }

    const onInputBlur = () => {
        setShowTipPop(false);
    }

    useEffect(() => {
        const check = inputRef?.current?.checkValidity() &&
            (ignoreRules || rules.current.required_score >= 4) &&
            (matchInput ? inputRef.current.value === matchInput : true);
        setValid(check);
        if (status && typeof status === 'function') {
            status(check);
        }
    }, [_props.value, matchInput]);

    return (
        <>
            <div className="comp-input">
                <div className="input-field--modern">
                    <div className="input-field--pwd">
                        <span className={`input-toggle--pwd ${visiblePasswd ? "ic-eye-open" : "ic-eye-close"}`} onClick={toggleVisible}></span>
                        <input {..._props}
                               placeholder={placeholder}
                               type={visiblePasswd ? 'text' : 'password'}
                               className={`input--modern ${!valid && isDirty.current ? "input--invalid" : ""}`}
                               onChange={onInputChange}
                               onFocus={onInputFocus}
                               onBlur={onInputBlur}
                               ref={inputRef}/>
                        <label className={`input-label--modern ${required ? 'required' : ''}`}>{label}</label>
                    </div>
                </div>
            </div>
            {
                showStrength &&
                <div className="comp-add-ons !mb-8">
                    <div className="pwd-strength">
                        <div className={`pwd-strength__meter ${strengthIndex[rules.current.rule_group.length].toLowerCase()}`}>
                            <span className="light-bar"></span>
                            <span className="light-bar"></span>
                            <span className="light-bar"></span>
                            <span className="light-bar"></span>
                        </div>
                        {
                            rules.current.rule_group.length > 0 && (
                                <div className="pwd-strength__lv">
                                    <span className="label">{t('@Strength@')}:</span>
                                    <span className="value">{t(`@${strengthIndex[rules.current.rule_group.length]}@`)}</span>
                                </div>
                            )
                        }
                    </div>
                </div>
            }
            {
                <div className={`comp-popover ${showTipPop ? "" : "hide"}`}>
                    <ul className="popover-menu popover-menu--pwd-rules">
                        <li className="popover-menu__head">{t('@Your password must have:@')}</li>
                        <li className="popover-menu__item">
                            <div className={`rule-group ${rules.current.rule_group.includes('length_reached') ? "done" : ""}`}>
                                <div className="rule-group__check"></div>
                                <div className="rule-group__label">{t('@6 or more cheracters@')}</div>
                            </div>
                        </li>
                        <li className="popover-menu__item">
                            <div className={`rule-group ${rules.current.rule_group.includes('lower_case') && rules.current.rule_group.includes('upper_case') ? "done" : ""}`}>
                                <div className="rule-group__check"></div>
                                <div className="rule-group__label">{t('@At least one upper & lowercase letters@')}</div>
                            </div>
                        </li>
                        <li className="popover-menu__item">
                            <div className={`rule-group ${rules.current.rule_group.includes('number') ? "done" : ""}`}>
                                <div className="rule-group__check"></div>
                                <div className="rule-group__label">{t('@At least one number@')}</div>
                            </div>
                        </li>
                        <li className="popover-menu__item">
                            <div className={`rule-group ${rules.current.rule_group.includes('qid_checked') ? "done" : ""}`}>
                                <div className="rule-group__check"></div>
                                <div className="rule-group__label">{t('@Passwords cannot be the same as your QID@')}</div>
                            </div>
                        </li>
                    </ul>
                </div>
            }
        </>
    );
}

export default PasswordInput;